/* Overview presentation only. personal-topic-report.js owns variants, validation and cart submission. */
(() => {
  'use strict';
  const init = page => {
  if (!page || page.dataset.pfPresentationReady === 'true') return;
  page.dataset.pfPresentationReady = 'true';
  const cleanups = [];
  page.pfCleanup = () => cleanups.forEach(fn => fn());
  const depthNote = page.querySelector('[data-pf-depth-note]');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const motion = page.querySelector('[data-pf-motion]');
  let manuallyPaused = false;
  const refreshMotion = () => {
    const paused = manuallyPaused || reduced.matches;
    page.dataset.pfMotionPaused = String(paused);
    motion.setAttribute('aria-pressed', String(paused));
    motion.querySelector('span').textContent = reduced.matches ? 'Motion reduced' : paused ? 'Resume motion' : 'Pause motion';
    motion.disabled = reduced.matches;
  };
  motion.hidden = false;
  motion.addEventListener('click', () => { manuallyPaused = !manuallyPaused; refreshMotion(); });
  reduced.addEventListener('change', refreshMotion);
  cleanups.push(() => reduced.removeEventListener('change', refreshMotion));
  refreshMotion();
  if ('IntersectionObserver' in window) {
    // Content is visible before observation and stays visible if observation fails.
    try {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        if (!reduced.matches && !manuallyPaused) entry.target.classList.add('pf-reveal-enter');
        observer.unobserve(entry.target);
      }), { threshold: .08 });
      page.querySelectorAll('[data-pf-reveal]').forEach(el => observer.observe(el));
      cleanups.push(() => observer.disconnect());
    } catch { /* The default HTML remains visible. */ }
  }

  const tabs = [...page.querySelectorAll('[data-pf-sample]')];
  const excerpts = {
    Basic: 'Deciding to begin does not require deciding what the finished work must prove. Try one fifteen-minute drawing of an object already nearby.',
    Focused: 'Is sharing part of the enjoyment, or has approval become a condition for continuing? Keep the first two sessions private if helpful, then compare ease of starting and interest rather than the quality of the pictures.',
    'In-Depth': 'There are at least three possible sources of friction: too many decisions, comparison with finished work or a schedule that exceeds actual energy. Change one condition at a time.'
  };
  const showSample = tab => {
    tabs.forEach(item => { item.setAttribute('aria-selected', String(item === tab)); item.tabIndex = item === tab ? 0 : -1; });
    page.querySelector('[data-pf-excerpt]').textContent = excerpts[tab.dataset.pfSample];
    page.querySelector('#SampleExcerpt').setAttribute('aria-labelledby', tab.id);
  };
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => showSample(tab));
    tab.addEventListener('keydown', event => {
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      if (next === undefined) return;
      event.preventDefault(); showSample(tabs[next]); tabs[next].focus();
    });
  });
  page.querySelector('[data-pf-sample-tabs]').hidden = false;

  const root = page.querySelector('[data-personal-report]');
  const form = root.querySelector('form');
  const mobile = matchMedia('(max-width:768px)');
  const syncOverlay = () => {
    const rect = root.getBoundingClientRect();
    document.body.toggleAttribute('data-pf-mobile-form-visible', mobile.matches && rect.top < (window.visualViewport?.height || innerHeight) && rect.bottom > 56);
  };
  window.addEventListener('scroll', syncOverlay, { passive: true });
  window.addEventListener('resize', syncOverlay);
  window.visualViewport?.addEventListener('resize', syncOverlay);
  cleanups.push(() => {
    window.removeEventListener('scroll', syncOverlay);
    window.removeEventListener('resize', syncOverlay);
    window.visualViewport?.removeEventListener('resize', syncOverlay);
    document.body.removeAttribute('data-pf-mobile-form-visible');
  });
  syncOverlay();
  // Keep native fields visible if the incumbent commerce script cannot initialize.
  if (!form || root.dataset.personalReportReady !== 'true') return;
  const $ = selector => form.querySelector(selector);
  const theme = $('[data-report-theme]'), question = $('[data-report-question]'), context = $('[data-report-context]');
  const material = $('[data-report-material]'), delivery = $('[name=personal_delivery]');
  const variantSelect = $('[name=id]'), submit = $('[data-report-submit]');
  const variants = JSON.parse(root.querySelector('[data-personal-variants]').textContent);
  const currency = root.dataset.currency;
  const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency });
  const price = variant => {
    if (!variant) return 'Unavailable';
    const formatted = formatter.format(variant.price / 100);
    return formatted.includes(currency) ? formatted : formatted + ' ' + currency;
  };
  const currentVariant = () => variants.find(variant => String(variant.id) === variantSelect.value);
  const panels = [...form.querySelectorAll('[data-pf-panel]')];
  const steps = [...form.querySelectorAll('[data-pf-step]')];
  const topicButtons = [...form.querySelectorAll('[data-pf-topic]')];
  const errors = new Map();
  let activeStep = 0;
  let refreshing = false;
  const disabledBeforeBusy = new Map();
  const setError = (control, message) => {
    let el = errors.get(control);
    if (!el) {
      el = document.createElement('span');
      el.className = 'pf-field-error';
      el.id = 'ReadingFieldError' + errors.size;
      control.closest('.pr-field,.pr-check').append(el);
      control.setAttribute('aria-describedby', [control.getAttribute('aria-describedby'), el.id].filter(Boolean).join(' '));
      errors.set(control, el);
    }
    el.textContent = message;
    el.hidden = !message;
    if (message) control.setAttribute('aria-invalid', 'true'); else control.removeAttribute('aria-invalid');
  };
  const revealControl = control => {
    const panel = control.closest('[data-pf-panel]');
    if (panel) setStep(Number(panel.dataset.pfPanel), false);
    let parent = control.parentElement;
    while (parent && parent !== form) { if (parent.tagName === 'DETAILS') parent.open = true; parent = parent.parentElement; }
    control.scrollIntoView({ block: 'center', behavior: 'instant' });
    control.focus({ preventScroll: true });
  };
  const validateQuestion = () => {
    const valid = question.value.trim().length >= 12;
    question.setCustomValidity(valid ? '' : 'Please write a question with at least 12 characters.');
    setError(question, question.validationMessage);
    if (!valid) revealControl(question);
    return valid;
  };
  const validateContext = () => {
    const carried = !$('[data-report-import]').hidden
      ? ($('[data-import-inputs]').value.trim() + ' ' + $('[data-import-notes]').value.trim()).trim() : '';
    const valid = context.value.trim().length >= 20 || carried.length >= 20 || material.value.trim().length >= 20;
    context.setCustomValidity(valid ? '' : 'Please share at least 20 characters of background, or include your starting details.');
    setError(context, context.validationMessage);
    if (!valid) revealControl(context);
    return valid;
  };
  const validateTopic = () => {
    const error = $('[data-pf-topic-error]');
    const valid = !!theme.value;
    error.textContent = valid ? '' : 'Choose a focus for your reading before continuing.';
    error.hidden = valid;
    if (!valid) { topicButtons[0].focus(); topicButtons[0].scrollIntoView({ block: 'center', behavior: 'instant' }); }
    return valid;
  };
  function setStep(step, moveFocus = true) {
    if(page.hasAttribute("data-pf-compact"))return;
    activeStep = step;
    panels.forEach((panel, index) => {
      panel.hidden = index !== step;
      panel.classList.toggle('pf-panel-enter', index === step);
    });
    steps.forEach((button, index) => {
      if (index === step) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
    });
    $('[data-pf-progress] i').style.width = ((step + 1) / 3 * 100) + '%';
    refreshReview();
    if (moveFocus) {
      const target = step === 0 ? question : step === 1 ? (!$('[data-report-import]').hidden ? $('[data-import-inputs]') : context) : $('#ReviewStepTitle');
      target.scrollIntoView({ block: 'center', behavior: reduced.matches || manuallyPaused ? 'instant' : 'smooth' });
      target.focus({ preventScroll: true });
    }
  }
  const advance = target => {
    if (target <= activeStep) { setStep(target); return; }
    if (!validateTopic() || !validateQuestion()) return;
    if (target === 2 && !validateContext()) return;
    setStep(target);
  };
  const row = (title, value, editStep, long = false) => {
    if (!value) return;
    const node = document.createElement('div');
    node.className = 'pf-review-row';
    const heading = document.createElement('b'); heading.textContent = title; node.append(heading);
    const content = document.createElement('p'); content.textContent = value;
    if (long) { const disclosure = document.createElement('details'); const summary = document.createElement('summary'); summary.textContent = 'Review the full ' + title.toLowerCase(); disclosure.append(summary, content); node.append(disclosure); } else node.append(content);
    if (editStep !== undefined) { const edit = document.createElement('button'); edit.type = 'button'; edit.className = 'pf-text-button'; edit.textContent = 'Edit ' + title.toLowerCase(); edit.addEventListener('click', () => setStep(editStep)); node.append(edit); }
    $('[data-pf-review]').append(node);
  };
  function refreshReview() {
    if(page.hasAttribute("data-pf-compact"))return;
    const box = $('[data-pf-review]');
    box.replaceChildren();
    row('Focus', theme.value);
    const v = currentVariant();
    row('Reading', v ? v.options.join(' · ') + ' · ' + price(v) : 'Unavailable');
    row('Question', question.value, 0);
    row('Background', context.value || 'Provided through starting details or material.', 1);
    row('Desired clarity', $('#ReportIntention').value, 1);
    row('Additional material', material.value, 1, material.value.length > 300);
    if (!$('[data-report-import]').hidden) {
      row('Source tool', $('[data-import-source]').textContent);
      for (const [field, title] of [['inputs', 'Tool inputs'], ['result', 'Free result'], ['notes', 'My reflection notes']]) {
        const value = $('[data-import-' + field + ']').value;
        row(title, value, 1, value.length > 300);
      }
    }
  }
  function refresh() {
    if(page.hasAttribute("data-pf-compact"))return;
    if (refreshing) return;
    refreshing = true;
    const v = currentVariant();
    const topicKey = theme.selectedOptions[0]?.dataset.topic;
    topicButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.pfTopic === topicKey)));
    $('[data-pf-summary-topic]').textContent = theme.value || 'Choose your focus';
    $('[data-pf-summary-depth]').textContent = v?.options[0] || 'Choose a reading option';
    $('[data-pf-total]').textContent = price(v);
    $('[data-pf-submit-price]').textContent = 'Your selected total: ' + price(v);
    $('[data-pf-depth-delivery]').closest('.pf-depth-grid').querySelectorAll('[data-pf-depth-delivery]').forEach(el => { el.textContent = delivery.value + ' option'; });
    const busy = submit.textContent.startsWith('Adding your report');
    const controls = form.querySelectorAll('input:not([type=hidden]),select,textarea,[data-pf-topic],[data-pf-hours],[data-pf-step],[data-pf-next],[data-pf-back],[data-import-remove]');
    controls.forEach(control => {
      if (busy) {
        if (!disabledBeforeBusy.has(control)) disabledBeforeBusy.set(control, control.disabled);
        control.disabled = true;
      } else if (disabledBeforeBusy.has(control)) {
        control.disabled = disabledBeforeBusy.get(control); disabledBeforeBusy.delete(control);
      }
    });
    root.setAttribute('aria-busy', String(busy));
    form.querySelectorAll('[name=personal_depth]').forEach(radio => {
      const option = variants.find(item => item.options[0] === radio.value && item.options[1] === delivery.value);
      const status = $('[data-pf-depth-status="' + radio.value + '"]');
      status.textContent = option?.available ? '' : 'Unavailable for this delivery window';
      status.hidden = !!option?.available;
      if (!busy) radio.disabled = !option?.available;
    });
    form.querySelectorAll('[data-pf-hours]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.pfHours === delivery.value));
      const option = variants.find(item => item.options[0] === $('[name=personal_depth]:checked').value && item.options[1] === button.dataset.pfHours);
      if (!busy) button.disabled = !option?.available;
    });
    const label = (busy ? 'Adding your report…' : 'Add my personal report to cart') + (v ? ' · ' + price(v) : '');
    if (submit.textContent !== label) submit.textContent = label;
    if (theme.value) $('[data-pf-topic-error]').hidden = true;
    const selectedDepth = form.querySelector('[name=personal_depth]:checked');
    const descriptions = { Basic: 'One clear question and a practical next step.', Focused: 'Your question in context.', 'In-Depth': 'Patterns, alternatives and a reflection plan.' };
    depthNote.textContent = (selectedDepth?.value || '') + ' · ' + (descriptions[selectedDepth?.value] || '');
    $('[data-pf-continue]').textContent = 'Continue with ' + (v?.options[0] || 'my reading');
    form.querySelectorAll('[data-pf-mobile-amount]').forEach(el => {
      const option = variants.find(item => item.options[0] === el.dataset.pfMobileAmount && item.options[1] === delivery.value);
      el.textContent = option ? formatter.formatToParts(option.price / 100).filter(part => part.type !== 'currency' && part.type !== 'literal').map(part => part.value).join('') : '—';
    });
    form.querySelectorAll('[data-pf-compare-price]').forEach(el => {
      const option = variants.find(item => item.options[0] === el.dataset.pfComparePrice && item.options[1] === delivery.value);
      el.textContent = price(option);
    });
    const base = variants.find(item => item.options[0] === v?.options[0] && item.options[1] === '24 hours');
    form.querySelectorAll('[data-pf-fee]').forEach(el => {
      const option = variants.find(item => item.options[0] === v?.options[0] && item.options[1] === el.dataset.pfFee);
      if (!option || !base) { el.textContent = 'Unavailable'; return; }
      const delta = option.price - base.price;
      el.textContent = delta === 0 ? 'Base price' : (delta > 0 ? '+' : '−') + formatter.format(Math.abs(delta) / 100);
    });
    if (activeStep === 2) refreshReview();
    refreshing = false;
  }
  topicButtons.forEach(button => button.addEventListener('click', () => {
    const option = [...theme.options].find(item => item.dataset.topic === button.dataset.pfTopic);
    theme.value = option.value;
    theme.dispatchEvent(new Event('change', { bubbles: true }));
  }));
  form.querySelectorAll('[data-pf-hours]').forEach(button => button.addEventListener('click', () => {
    delivery.value = button.dataset.pfHours;
    delivery.dispatchEvent(new Event('change', { bubbles: true }));
  }));
  form.querySelectorAll('[data-pf-next]').forEach(button => button.addEventListener('click', () => advance(Number(button.dataset.pfNext))));
  form.querySelectorAll('[data-pf-back]').forEach(button => button.addEventListener('click', () => setStep(Number(button.dataset.pfBack))));
  steps.forEach(button => button.addEventListener('click', () => advance(Number(button.dataset.pfStep))));
  form.addEventListener('change', refresh);
  form.addEventListener('input', event => {
    const control = event.target;
    if (errors.has(control)) setError(control, '');
    if (control.matches('[data-import-inputs],[data-import-notes],[data-report-material]')) {
      context.setCustomValidity(''); if (errors.has(context)) setError(context, '');
    }
    if (activeStep === 2) refreshReview();
  });
  form.addEventListener('invalid', event => {
    if(page.hasAttribute('data-pf-compact'))return;
    event.preventDefault();
    if (event.target === theme) { validateTopic(); return; }
    setError(event.target, event.target.validationMessage);
    revealControl(event.target);
  }, true);
  form.addEventListener('submit', event => {
    if(page.hasAttribute('data-pf-compact'))return;
    if (activeStep !== 2) {
      event.preventDefault(); event.stopImmediatePropagation(); advance(activeStep + 1); return;
    }
    if (!validateTopic() || !validateQuestion() || !validateContext()) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  $('[data-import-remove]').addEventListener('click', () => {
    // The existing removal listener runs first, clears storage and preserves edited fields.
    setStep(0, false); refresh();
  });
  const params = new URLSearchParams(location.search);
  const focus = params.get('focus');
  if (!params.get('topic') && !params.get('reading') && focus) {
    const aliases = { relationships: 'love', direction: 'career', self: 'self-discovery' };
    const option = [...theme.options].find(item => item.dataset.topic === (aliases[focus] || focus) || item.value === focus);
    if (option) { theme.value = option.value; theme.dispatchEvent(new Event('change', { bubbles: true })); }
  }
  if (focus) { $('[data-pf-focus-note]').textContent = 'Your starting focus: ' + focus; $('[data-pf-focus-note]').hidden = false; }
  const selectedExtra = topicButtons.find(button => button.dataset.pfTopic === theme.selectedOptions[0]?.dataset.topic && button.closest('.pf-extra-topics'));
  if (selectedExtra) selectedExtra.closest('details').open = true;
  $('.pf-native-focus').hidden = true;
  $('[data-pf-topics]').hidden = false;
  $('.pf-delivery-native').hidden = true;
  $('[data-pf-delivery]').hidden = false;
  $('[data-pf-steps]').hidden = false;
  $('[data-pf-progress]').hidden = false;
  $('[data-pf-review]').hidden = false;
  $('[data-pf-submit-price]').hidden = false;
  form.querySelectorAll('[data-pf-actions],[data-pf-back]').forEach(el => { el.hidden = false; });
  const totalObserver = new MutationObserver(refresh);
  totalObserver.observe($('[data-report-total]'), { childList: true, characterData: true, subtree: true });
  totalObserver.observe(submit, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  cleanups.push(() => totalObserver.disconnect());
  setStep(0, false);
  refresh();
  root.dataset.personalFlowReady = 'true';
  window.WotfPersonalMobileFlow?.mount(page);
  cleanups.push(()=>page.pmCleanup?.());
  if (params.has('topic') || params.has('focus') || params.has('reading')) {
    requestAnimationFrame(() => root.scrollIntoView({ block: 'start', behavior: 'instant' }));
  }
  };
  const initAll = scope => {
    if (scope.matches?.('.pr-page.pc.pf')) init(scope);
    scope.querySelectorAll('.pr-page.pc.pf').forEach(init);
  };
  initAll(document);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.pr-page.pc.pf').forEach(page => page.pfCleanup?.()));
})();
